import { useNavigate } from 'react-router-dom'
import { useAuth } from '../store/AuthContext'
import { QuarterlySummaryEmployee } from '../services/types'

interface RankingTableProps {
  employees: QuarterlySummaryEmployee[]
  isLoading?: boolean
  highlightEmployeeId?: number
}

export const RankingTable = ({
  employees,
  isLoading = false,
  highlightEmployeeId,
}: RankingTableProps) => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const isViewer = user?.role === 'viewer'

  // Sort by average (descending)
  const sortedEmployees = [...employees].sort((a, b) => b.average - a.average)

  // Calculate ownership percentage
  const totalAccumulated = sortedEmployees.reduce((sum, e) => sum + Math.max(e.total_accumulated, 0), 0)

  const getMedalIcon = (position: number) => {
    if (position === 1) return '\u{1F947}'
    if (position === 2) return '\u{1F948}'
    if (position === 3) return '\u{1F949}'
    return position
  }

  const handleRowClick = (employeeId: number) => {
    // Viewers can only navigate to their own profile
    if (isViewer && employeeId !== highlightEmployeeId) return
    navigate(`/funcionarios/${employeeId}`)
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (employees.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
        <p className="text-center text-gray-500 dark:text-gray-400">
          Nenhum dado de ranking disponível para este período.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Posição
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Funcionário
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Empresa
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Média Trimestral
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Total Acumulado
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                % Ownership
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedEmployees.map((employee, index) => {
              const position = index + 1
              const isHighlighted = employee.employee_id === highlightEmployeeId
              const canClick = !isViewer || employee.employee_id === highlightEmployeeId
              const ownershipPct = totalAccumulated > 0 && employee.total_accumulated > 0
                ? (employee.total_accumulated / totalAccumulated) * 100
                : 0

              return (
                <tr
                  key={employee.employee_id}
                  onClick={() => canClick && handleRowClick(employee.employee_id)}
                  className={`transition-colors ${
                    canClick ? 'cursor-pointer' : 'cursor-default'
                  } ${
                    isHighlighted
                      ? 'bg-primary/5 dark:bg-primary/10'
                      : canClick
                      ? 'hover:bg-gray-50 dark:hover:bg-gray-900'
                      : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {employee.average !== 0 ? getMedalIcon(position) : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
                        {employee.employee_name[0]}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {employee.employee_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {employee.company_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className={`text-sm font-semibold ${
                      employee.average > 0
                        ? 'text-success'
                        : employee.average < 0
                        ? 'text-danger'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {employee.average > 0 ? '+' : ''}{employee.average}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {employee.total_accumulated > 0 ? '+' : ''}{employee.total_accumulated}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm font-semibold text-primary">
                      {ownershipPct > 0 ? `${ownershipPct.toFixed(2)}%` : '-'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default RankingTable
